import { v4 as uuid } from "uuid";
import clsx from "clsx";
// MUI
import { Box, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
// Types
import { IColumnDefinition, IInformationCard } from "@/Washouts/types/commons";
import { accountFormat, getFormatAmount, isNegative } from "@/Common/helpers/number";
import { getFormatUTCDate } from "@/Common/helpers/Dates";
import { measureTextWidth } from "@/Common/helpers/measure";
import { TooltipTitleWrapper } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import generateTailwindVariableClasses from "@/Common/helpers/TailwindCSS";
import { DATE_FORMAT } from "@/Constants/date";

const DEFAULT_WIDTH = 150;
const DEFAULT_ROW_HEIGHT = 24;
const TRUNCATE_CORRECTION = 5;

function decimalFormat(value: unknown, decimals: number) {
	const val = value ?? 0;
	return getFormatAmount(val, decimals);
}

function moneyFormat(value: unknown) {
	const numberValue = Number(value);
	if (isNaN(numberValue)) {
		return "-";
	}
	const formattedValue = accountFormat(Math.abs(numberValue));
	return numberValue < 0 ? `(${formattedValue})` : String(formattedValue);
}

function integerFormat(value: unknown) {
	const numberValue = Number(value);
	if (isNaN(numberValue)) {
		return "0";
	}
	return getFormatAmount(numberValue, 0);
}

function hoursFormat(value: unknown) {
	const hours = Math.floor(Number(value ?? 0));
	const decimalMinutes = Number(value ?? 0) - hours;
	const minutes = +Number(Math.round(decimalMinutes * 60)).toFixed(0);
	if (minutes) {
		return `${hours} hrs ${minutes} mins`;
	}
	return `${hours} hrs`;
}

function getValue(rawValue: unknown, columnDefinition: IColumnDefinition): string {
	switch (columnDefinition.type) {
		case "money":
			return moneyFormat(rawValue);
		case "decimals_2": {
			return decimalFormat(rawValue, 2);
		}
		case "decimals_1": {
			return decimalFormat(rawValue, 1);
		}
		case "percentage": {
			return `${decimalFormat(rawValue, 2)}%`;
		}
		case "date": {
			return rawValue ? getFormatUTCDate(rawValue as string | number | Date, DATE_FORMAT.usDateSlashShortYear) : "--/--/--";
		}
		case "integer": {
			return integerFormat(rawValue);
		}
		case "hours": {
			return hoursFormat(rawValue);
		}
		default:
			return rawValue ? String(rawValue) : "";
	}
}

function computeWidth(columnWidth?: number) {
	if (!columnWidth) {
		return `${DEFAULT_WIDTH}px`;
	}
	if (columnWidth < 0) {
		return "100%";
	}
	return `${columnWidth}px`;
}

const defaultCellRender: IColumnDefinition["cellRender"] = (row, column) => {
	const cellValue = getValue(row[column.field], column);
	const cellContent = (
		<Typography
			fontSize="14px"
			align={column.alignCell}
			className={clsx({
				"!font-[600] !font-montserrat relative truncate": true,
				"text-default": !isNegative(row[column.field]),
				"text-[color:--text-alert-color]": isNegative(row[column.field]),
				"before:content-['$'] before:absolute before:left-0": column.type === "money",
			})}
			sx={{ width: computeWidth(column.width) }}
		>
			{cellValue}
		</Typography>
	);
	if (column.autoTooltip && measureTextWidth(cellValue, 16, "Montserrat") >= (column.width ?? DEFAULT_WIDTH) - TRUNCATE_CORRECTION) {
		return (
			<Tooltip arrow={true} title={<TooltipTitleWrapper text={cellValue} />}>
				{cellContent}
			</Tooltip>
		);
	}
	return cellContent;
};

function defaultHeaderRender(column: IColumnDefinition) {
	return (
		<Typography
			fontSize="14px"
			lineHeight="19.5px"
			align={column.alignHeader}
			className="text-default !font-[500] !font-montserrat"
			sx={{ width: computeWidth(column.width) }}
		>
			{column.header}
		</Typography>
	);
}

export default function InformationCard({
	title,
	loading,
	columns,
	rows,
	cellMargin = 16,
	cardPadding = 24,
	...props
}: Readonly<IInformationCard>) {
	const fillerPadding = (Math.max(props.minRows ?? 0, rows.length) - rows.length) * DEFAULT_ROW_HEIGHT;
	const totalColumnWidth = columns.reduce((totalWidth, column) => {
		const columnWidth = column.width ?? DEFAULT_WIDTH;
		return totalWidth + columnWidth;
	}, 0);
	const content = loading ? (
		<>
			<Skeleton variant="text" height="30px" width={`${totalColumnWidth}px`} className="!mb-[24px]" />
			<Box className="flex flex-row gap-x-[calc(2*var(--internal-cell-margin)+1px)] pt-[4px]">
				{columns.map((column) => (
					<Box key={`${column.field}`} className="flex flex-col gap-y-[4px]">
						<Skeleton variant="text" height="27px" width={(column.width ?? 0) > 0 ? column.width : DEFAULT_WIDTH} />
						<Skeleton variant="text" height="27px" width={(column.width ?? 0) > 0 ? column.width : DEFAULT_WIDTH} />
					</Box>
				))}
			</Box>
		</>
	) : (
		<>
			<Typography
				fontSize="24px"
				className="!mb-[24px] text-primary !font-montserrat !font-[500] !leading-[1.75rem]"
				dangerouslySetInnerHTML={{ __html: title }}
			/>
			<TableContainer sx={{ paddingBottom: `${fillerPadding}px`, marginBottom: props?.heightAdjustment }}>
				<Table>
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell
									key={`${column.field}`}
									className={clsx({
										"!align-top border-disabled !border-b-0 !py-[8px] !px-[--internal-cell-margin]": true,
										"first:!pl-0 first:!pr-[--internal-cell-margin]": columns.length > 1,
										"border-r last:!pr-0 last:!pl-[--internal-cell-margin] last:border-r-0": columns.length > 1,
										"!pl-0 !pr-0": columns.length === 1,
									})}
								>
									{column.columnRender ? column.columnRender(column) : defaultHeaderRender?.(column)}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row) => (
							<TableRow key={`row-${uuid()}`}>
								{columns.map((column) => (
									<TableCell
										key={`cell-${column.field}`}
										className={clsx({
											"overflow-hidden border-disabled !border-b-0 !py-0 !px-[--internal-cell-margin]": true,
											"first:!pl-0 first:!pr-[--internal-cell-margin]": columns.length > 1,
											"border-r last:border-r-0 last:!pr-0 last:!pl-[--internal-cell-margin]": columns.length > 1,
											"!pl-0 !pr-0": columns.length === 1,
										})}
									>
										{column.cellRender ? column.cellRender(row, column) : defaultCellRender?.(row, column)}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
	return (
		<Box
			sx={{
				...generateTailwindVariableClasses("--internal-cell-margin", `${cellMargin}px`),
				...generateTailwindVariableClasses("--internal-card-padding", `${cardPadding}px`),
			}}
		>
			<Box className="rounded-[10px] shadow-[0_1px_6px_0_#00000033] py-[24px] px-[--internal-card-padding]">{content}</Box>
		</Box>
	);
}
